import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import _get from "lodash.get";
import AddCircle from "@material-ui/icons/AddCircle";

import {
  closeModalDialog,
  openModalDialog,
  showSnackNotificationAction,
} from "../../../../../actions/layoutActions";
import {
  initDataComponent,
  performRetrieveListRequest,
  performDeleteRequest,
  setReload,
} from "../../../../../actions/dataComponentActions";
import propTypes from "../../../../../constants/propTypes";
import {
  getDataComponent,
  getDataComponentFlattenedRequestState,
} from "../../../../../reducers/dataComponentReducer";
import { getBluechipResourcesByType } from "../../../../../utils/bluechipUtils";
import { Paper } from "../../../../mui/core";
import { LoaderContext } from "../../../../ui/Loader";
import { useDeleteProcessRequests } from "../../../../hooks/useProcessRequest";
import { convertServerErrorToFormikErrors } from "../../../../../utils/formValidationUtils";
import withRenderOnPermissionActive from "../../../../hocs/withRenderOnPermissionActive";
import { BWGridAPI, GridHeader, GridTable } from "../../../../ui/BWGrid";
import Certification from "../../../../../models/Certification";

export const vendorCertificationDataComponentId =
  "dataComponentVendorCertification";

export const columns = ["name", "description"];

export const columnOptions = {
  name: { title: "Name", filter: true },
  description: { title: "Description", filter: true },
};

const rowMenu = ({ handleVendorDeleteCertification, handleOpenEditModal }) => [
  {
    text: "Edit",
    onClick: handleOpenEditModal,
  },
  {
    text: "Delete",
    onClick: handleVendorDeleteCertification,
  },
];

const actions = onOpenCreateModal => [
  {
    text: "Create Certification",
    icon: <AddCircle />,
    handler: onOpenCreateModal,
  },
];

export const VendorCertificationsContainer = ({
  dataComponent,
  flattenedDataComponent,
  loading,
  performDeleteRequest,
  setReload,
  showSnackNotificationAction,
  openModalDialog,
  closeModalDialog,
}) => {
  useDeleteProcessRequests(dataComponent, {
    onSuccess: () => {
      showSnackNotificationAction(
        "The vendor certification has been deleted successfully"
      );
      setReload(vendorCertificationDataComponentId, true);
    },
    onError: error => {
      const convertedError = convertServerErrorToFormikErrors(error);
      showSnackNotificationAction(convertedError["certifications"]);
    },
  });

  const handleOpenCreateModal = useCallback(() => {
    openModalDialog(
      "Create Vendor Certification",
      "CreateVendorCertification",
      {},
      true,
      true
    );
  }, [openModalDialog]);

  const handleOpenEditModal = useCallback(
    vendorCertification => {
      openModalDialog(
        "Edit Vendor Certification",
        "EditVendorCertification",
        { vendorCertification },
        true,
        true
      );
    },
    [openModalDialog]
  );

  const handleVendorDeleteCertification = useCallback(
    certification => {
      openModalDialog(
        "Delete Vendor Certification",
        "GeneralConfirmation",
        {
          title: "Are you sure you want to remove this vendor certification?",
          sendButtonText: "Confirm",
          onSubmit: () => {
            performDeleteRequest(
              vendorCertificationDataComponentId,
              certification.id
            );
            closeModalDialog();
          },
        },
        true,
        true
      );
    },
    [closeModalDialog, openModalDialog, performDeleteRequest]
  );

  return (
    <Paper>
      <LoaderContext.Provider value={loading}>
        <BWGridAPI
          dataComponent={flattenedDataComponent}
          model={Certification}
          defaultSorting={[
            {
              columnName: "name",
              direction: "asc",
            },
          ]}
          apiRoute="certifications"
          includes={[]}
        >
          <GridHeader
            headerText={`${flattenedDataComponent.totalRows} Vendor Certifications`}
            actions={actions(handleOpenCreateModal)}
          />
          <GridTable
            columns={columns}
            columnOptions={columnOptions}
            rowMenu={rowMenu({
              handleVendorDeleteCertification,
              handleOpenEditModal,
            })}
          />
        </BWGridAPI>
      </LoaderContext.Provider>
    </Paper>
  );
};

VendorCertificationsContainer.propTypes = {
  dataComponent: propTypes.dataComponent,
  flattenedDataComponent: propTypes.dataComponent,
  loading: PropTypes.bool,
  openModalDialog: PropTypes.func.isRequired,
  performDeleteRequest: PropTypes.func.isRequired,
  setReload: PropTypes.func.isRequired,
  showSnackNotificationAction: PropTypes.func.isRequired,
  closeModalDialog: PropTypes.func.isRequired,
};

export const mapStateToProps = state => {
  const flattenedDataComponent = getDataComponentFlattenedRequestState(
    vendorCertificationDataComponentId,
    state
  );

  const dataComponent = getDataComponent(
    vendorCertificationDataComponentId,
    state
  );

  const requirements =
    getBluechipResourcesByType(vendorCertificationDataComponentId, state) || [];
  return {
    dataComponent,
    flattenedDataComponent,
    loading: _get(flattenedDataComponent, "loading") || !requirements,
  };
};

export const mapDispatchToProps = {
  initDataComponent,
  performRetrieveListRequest,
  performDeleteRequest,
  setReload,
  showSnackNotificationAction,
  openModalDialog,
  closeModalDialog,
};

export default withRenderOnPermissionActive(
  connect(mapStateToProps, mapDispatchToProps)(VendorCertificationsContainer),
  ["manage-vendor-certifications"]
);
